/**
 * ResourcesPage — Comprehensive curated resources
 *
 * Categorised resources with search, save, and rich metadata.
 * Books, podcasts, TED Talks, online resources, courses,
 * practice communities, apps, and crisis support.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  BookOpen,
  Headphones,
  Video,
  Globe,
  Award,
  Users,
  Smartphone,
  AlertCircle,
  ExternalLink,
  Star,
  Search,
  Bookmark,
  Clock,
  ChevronRight,
  Mic,
  Coffee,
  Shield,
  Phone,
  MapPin,
  Calendar,
  Brain,
} from 'lucide-react';

import CharacterAvatar from '../components/characters/CharacterAvatar';
import Card from '../components/shared/Card';
import { CRISIS_SUPPORT, DEFAULT_COUNTRY } from '../data/crisisSupport';
import { useProgress } from '../context/ProgressContext';
import { useGamification } from '../context/GamificationContext';

/* ─────────────────────────────────────────────
   DATA
   ───────────────────────────────────────────── */

const books = {
  essential: [
    {
      title: 'Daring Greatly',
      author: 'Brené Brown',
      year: '2012',
      description: 'Brown\'s case for vulnerability, built on her interviews about shame and connection. She argues that letting yourself be seen, with no guarantee of how it will go, takes courage, and that the alternative is standing outside your own life looking in.',
      category: 'Vulnerability & Courage',
      character: 'philemon',
      whyRead: 'Brown counts being vulnerable as an act of courage, where most of us were taught to count it as weakness. Read it before any speaking situation that frightens you.',
      keyTakeaway: 'Being willing to be seen when you cannot control the outcome is a form of courage.',
      difficulty: 'Accessible',
      url: 'https://openlibrary.org/isbn/1592408419',
    },
    {
      title: 'Quiet: The Power of Introverts in a World That Can\'t Stop Talking',
      author: 'Susan Cain',
      year: '2012',
      description: 'Cain argues that a culture built around outgoing people undervalues what introverts do well: listening, creating, working alone. For anyone who has been told to "speak up more".',
      category: 'Introversion & Strength',
      character: 'philemon',
      whyRead: 'Cain gave quiet people words for something many had felt and never said. The loudest voice in a room is not always the one worth most.',
      keyTakeaway: 'Being the best talker in the room and having the best ideas are two different things.',
      difficulty: 'Accessible',
      url: 'https://openlibrary.org/isbn/0141029196',
    },
    {
      title: 'Talk Like TED',
      author: 'Carmine Gallo',
      year: '2014',
      description: 'Gallo, a speaking coach, broke down hundreds of TED talks and interviewed popular speakers and researchers, and came away with nine things the best talks share. The book is his own and is not endorsed by TED.',
      category: 'Presentation Craft',
      character: 'theron',
      whyRead: 'Gallo works from talks that held a room, and you can watch every one of them. Read a chapter, then go and see it done.',
      keyTakeaway: 'A talk that lands is engaging, persuasive and memorable, and each of those can be practised.',
      difficulty: 'Practical & Accessible',
      url: 'https://openlibrary.org/isbn/1250061539',
    },
    {
      title: 'Resonate',
      author: 'Nancy Duarte',
      year: '2010',
      description: 'How to build a presentation the way a story is built, with conflict and resolution. Duarte takes well-known speeches apart to show the shape they share.',
      category: 'Message Structure',
      character: 'theron',
      whyRead: 'Duarte makes the audience the hero and the presenter the mentor. Once you see a talk that way, you stop performing at people and start guiding them.',
      keyTakeaway: 'A strong presentation moves back and forth between what is and what could be.',
      difficulty: 'Moderate',
      url: 'https://openlibrary.org/isbn/0470632011',
    },
    {
      title: 'Crucial Conversations',
      author: 'Kerry Patterson, Joseph Grenny, Ron McMillan, Al Switzler',
      year: '2002',
      description: 'Tools for talking when stakes are high, opinions differ and emotions run strong. A practical manual for the conversations that matter most.',
      category: 'Difficult Conversations',
      character: 'xenia',
      whyRead: 'The hardest talking is done across a table with something real at stake, and rarely on a stage. This book gives you steps to follow when you get there.',
      keyTakeaway: 'You can say a hard thing honestly and respectfully, and make it safe enough for the other person to stay in the conversation.',
      difficulty: 'Practical',
      url: 'https://openlibrary.org/isbn/1260474186',
    },
    {
      title: 'Presence: Bringing Your Boldest Self to Your Biggest Challenges',
      author: 'Amy Cuddy',
      year: '2015',
      description: 'Cuddy on how to stay yourself in a high-pressure moment, and on the part body language plays. Read it knowing where the evidence stands: an upright, open posture makes people report feeling more confident; the famous hormone claims did not hold up.',
      category: 'Physical Presence',
      character: 'theron',
      whyRead: 'The body knows things the mind hasn\'t caught up with yet. Take her practical advice for the minutes before a hard moment, and leave the hormones out of it.',
      keyTakeaway: 'Presence is the state in which you stop worrying about the impression you make on others and attend to the one you make on yourself.',
      difficulty: 'Accessible',
      url: 'https://openlibrary.org/isbn/1409156028',
    },
    {
      title: 'Nonviolent Communication',
      author: 'Marshall B. Rosenberg',
      year: '1999',
      description: 'Rosenberg\'s method for speaking and listening without blame. It has four parts: what you observe, what you feel, what you need and what you ask for.',
      category: 'Compassionate Communication',
      character: 'xenia',
      whyRead: 'Rosenberg teaches that how we say a thing can open a conversation or close it. The choice is ours each time.',
      keyTakeaway: 'Say what you observed, what you feel, what you need and what you would like, and leave the blame out.',
      difficulty: 'Moderate',
      url: 'https://openlibrary.org/isbn/189200528X',
    },
    {
      title: 'TED Talks: The Official TED Guide to Public Speaking',
      author: 'Chris Anderson',
      year: '2016',
      description: 'The head of TED on how a talk is built and delivered, drawing on his work behind the scenes with its speakers. There is, he says, no set formula.',
      category: 'Public Speaking',
      character: 'xenia',
      whyRead: 'Anderson wants you to give the talk that only you can give. A good host asks the same of every guest.',
      keyTakeaway: 'There is no set formula, and no two talks should be the same.',
      difficulty: 'Accessible',
      url: 'https://openlibrary.org/isbn/1472228065',
    },
  ],
  fearAndConfidence: [
    {
      title: 'Feel the Fear and Do It Anyway',
      author: 'Susan Jeffers',
      year: '1987',
      description: 'A guide to turning fear and indecision into action. Jeffers starts from a blunt premise: the fear does not go away while you keep growing, so you learn to act with it there.',
      category: 'Fear & Courage',
      character: 'philemon',
      whyRead: 'The title is the whole lesson. If you keep one sentence from these evenings, keep that one.',
      keyTakeaway: 'The fear will not go away as long as you keep growing, and the way to be rid of the fear of doing something is to go out and do it.',
      difficulty: 'Accessible',
      url: 'https://openlibrary.org/isbn/0091907071',
    },
    {
      title: 'The Body Keeps the Score',
      author: 'Bessel van der Kolk',
      year: '2014',
      description: 'Van der Kolk draws on decades of clinical work to argue that trauma changes both body and brain, and describes a range of treatments. Worth reading if you suspect an old experience sits behind your silence.',
      category: 'Trauma & the Body',
      character: 'philemon',
      whyRead: 'Some silences go deeper than nerves. Van der Kolk\'s case is that the body goes on carrying what the mind has put away. Read it slowly.',
      keyTakeaway: 'Keeping secrets from yourself has a cost, and letting yourself know what you know takes courage.',
      difficulty: 'Challenging',
      url: 'https://openlibrary.org/isbn/0143127748',
    },
    {
      title: 'Man\'s Search for Meaning',
      author: 'Viktor E. Frankl',
      year: '1946',
      description: 'Frankl\'s account of his time in the Nazi concentration camps, and the theory he drew from it: that the deepest human drive is to find meaning. Read it before you ask what you want your voice to be for.',
      category: 'Purpose & Meaning',
      character: 'philemon',
      whyRead: 'Frankl writes of the will to find meaning in the worst of circumstances. A speaker who knows what the words are for stands steadier.',
      keyTakeaway: 'People are driven above all by the search for what they find meaningful, and that search can survive the worst adversity.',
      difficulty: 'Moderate',
      url: 'https://openlibrary.org/isbn/080701429X',
    },
    {
      title: 'The Confidence Gap',
      author: 'Russ Harris',
      year: '2010',
      description: 'Confidence seen through acceptance and commitment therapy (ACT). Harris, a therapist and coach, teaches you to make room for fear and act on what matters to you without waiting for the fear to leave first.',
      category: 'Confidence & ACT',
      character: 'philemon',
      whyRead: 'Harris holds that the actions of confidence come first and the feeling follows later. Pure Philemon.',
      keyTakeaway: 'Confidence comes from a changed relationship with fear. The fear itself can stay.',
      difficulty: 'Accessible & Practical',
      url: 'https://openlibrary.org/isbn/1590309235',
    },
  ],
  craftAndStructure: [
    {
      title: 'slide:ology',
      author: 'Nancy Duarte',
      year: '2008',
      description: 'The art and science of presentation slides. Duarte teaches visual thinking: how to turn an idea into a sketch, a diagram or a slide an audience can take in.',
      category: 'Visual Communication',
      character: 'theron',
      whyRead: 'Structure and visual clarity are forms of respect for your audience.',
      keyTakeaway: 'Think visually first, then make slides the audience can process.',
      difficulty: 'Practical',
      url: 'https://openlibrary.org/isbn/0596522347',
    },
    {
      title: 'Steal the Show',
      author: 'Michael Port',
      year: '2015',
      description: 'Port, a former actor, applies an actor\'s methods to speeches, job interviews and pitches: any moment when you are, in effect, on stage.',
      category: 'Performance',
      character: 'theron',
      whyRead: 'Port treats a pitch, an interview and a speech as performances, and performing as a trade that can be learnt.',
      keyTakeaway: 'Prepare as an actor would, and rehearse until the words are yours.',
      difficulty: 'Accessible & Practical',
      url: 'https://openlibrary.org/isbn/0544800842',
    },
    {
      title: 'Made to Stick',
      author: 'Chip Heath & Dan Heath',
      year: '2007',
      description: 'Why some ideas survive and others die. The Heath brothers find six traits that memorable ideas share and show how to build them into your own message.',
      category: 'Memorable Messages',
      character: 'theron',
      whyRead: 'If your audience can\'t remember your message, you haven\'t delivered one. This book teaches you how to make ideas stick.',
      keyTakeaway: 'The curse of knowledge: once you know something it is hard to imagine not knowing it, which is why experts are so often hard to follow.',
      difficulty: 'Accessible',
      url: 'https://openlibrary.org/isbn/1400064287',
    },
    {
      title: 'The Quick and Easy Way to Effective Speaking',
      author: 'Dale Carnegie, revised by Dorothy Carnegie',
      year: '1962',
      description: 'A streamlined edition of Carnegie\'s public speaking classic, revised by Dorothy Carnegie. It sets out, step by step, how to build confidence and courage in front of a room.',
      category: 'Speaking Fundamentals',
      character: 'theron',
      whyRead: 'Before the brain scans and the TED stage, there was Carnegie. Plain instruction from another age, and still in print.',
      keyTakeaway: 'Good public speakers are made, not born.',
      difficulty: 'Accessible',
      url: 'https://openlibrary.org/isbn/0749305770',
    },
  ],
  connectionAndListening: [
    {
      title: 'The Art of Gathering',
      author: 'Priya Parker',
      year: '2018',
      description: 'How we meet and why it matters. Parker, a facilitator, argues that we lean too much on routine when we gather, and shows what changes when a host puts real thought and structure into it.',
      category: 'Gatherings & Connection',
      character: 'xenia',
      whyRead: 'Parker writes about gatherings the way Xenia thinks about her courtyard: as something a host makes on purpose.',
      keyTakeaway: 'A gathering comes alive when the host has thought about it, given it a structure and cared about the people in the room.',
      difficulty: 'Engaging',
      url: 'https://openlibrary.org/isbn/1594634920',
    },
    {
      title: 'Radical Candor',
      author: 'Kim Scott',
      year: '2017',
      description: 'A management book about feedback. Scott\'s rule is to care personally and challenge directly at the same time, and it travels well beyond the office.',
      category: 'Honest Communication',
      character: 'xenia',
      whyRead: 'Scott\'s point is that honesty and kindness can sit at the same table. Telling someone a hard truth can be a way of caring for them.',
      keyTakeaway: 'Radical Candor means caring personally and challenging directly, both at once.',
      difficulty: 'Practical',
      url: 'https://openlibrary.org/isbn/1250235375',
    },
    {
      title: 'You\'re Not Listening',
      author: 'Kate Murphy',
      year: '2020',
      description: 'A journalist\'s look at why we have stopped listening and what it costs us, with lessons from people who listen for a living, among them a bartender, a radio producer and a focus group moderator.',
      category: 'Listening',
      character: 'xenia',
      whyRead: 'Murphy went looking for the best listeners she could find and wrote down what they do. Speaking without listening is only noise.',
      keyTakeaway: 'Most of us do not listen as often or as well as we would like, and it can be learnt.',
      difficulty: 'Accessible',
      url: 'https://openlibrary.org/isbn/1250297192',
    },
    {
      title: 'Just Listen',
      author: 'Mark Goulston',
      year: '2009',
      description: 'How to get through to people who seem unreachable. Goulston, a psychiatrist who also trained hostage negotiators, draws on both kinds of work.',
      category: 'Influence & Connection',
      character: 'xenia',
      whyRead: 'Goulston starts where a good host starts: find out where the other person is before you ask them to move.',
      keyTakeaway: 'People start to listen once they feel listened to.',
      difficulty: 'Practical',
      url: 'https://openlibrary.org/isbn/0814436471',
    },
  ],
};

const podcasts = [
  {
    title: 'Unlocking Us with Brené Brown',
    host: 'Brené Brown',
    description: 'Conversations about courage, vulnerability and connection, some with guests and some with Brown talking straight to the listener. There have been no new episodes since October 2024, and the whole archive is still there.',
    category: 'Vulnerability & Courage',
    character: 'philemon',
    episodeLength: '30–75 min',
    frequency: 'Archive, 2020 to 2024',
    whyListen: 'Brown asks the questions most people are afraid to ask, and her guests are brave enough to answer honestly.',
    bestEpisode: 'Brené on FFTs, the first episode, on staying with the awkward first time you do anything',
    level: 'All levels',
    free: true,
  },
  {
    title: '10% Happier with Dan Harris',
    host: 'Dan Harris',
    description: 'Interviews with meditation teachers and scientists. The host is a former news anchor who took up meditation after a panic attack on live television and wrote his book for fellow sceptics.',
    category: 'Mindfulness & Anxiety',
    character: 'philemon',
    episodeLength: '45–75 min',
    frequency: 'Several a week',
    whyListen: 'Harris came to this as a sceptic with nerves of his own, and he says so. He asks the questions a doubter would ask.',
    bestEpisode: 'Comedian Aparna Nancherla on impostor syndrome, anger, social anxiety and stage fright (September 2023)',
    level: 'Beginner-friendly',
    free: true,
  },
  {
    title: 'The Moth',
    host: 'The Moth',
    description: 'True stories, told live and without notes. Each teller stands alone at a microphone in front of a room of strangers. The feed carries The Moth Radio Hour and shorter episodes of The Moth Podcast.',
    category: 'Storytelling & Authenticity',
    character: 'xenia',
    episodeLength: '15 or 50 min',
    frequency: 'Twice a week',
    whyListen: 'Every episode makes the same case: a story holds a room when the teller is willing to be honest in it.',
    bestEpisode: 'Any episode. Listen for how plainly people speak when the story is their own.',
    level: 'All levels',
    free: true,
  },
  {
    title: 'The TED Interview',
    host: 'Chris Anderson',
    description: 'Chris Anderson, the head of TED, interviews thinkers and makers at length about their work and, now and then, asks them to defend it. There have been no new episodes since May 2024.',
    category: 'Ideas & Communication',
    character: 'theron',
    episodeLength: '40–65 min',
    frequency: 'Archive, 2018 to 2024',
    whyListen: 'A short talk leaves a lot out. Here the speaker has to explain the idea again, slowly, to someone who pushes back.',
    bestEpisode: 'Any episode with a speaker whose talk you have already watched',
    level: 'All levels',
    free: true,
  },
  {
    title: 'On Being with Krista Tippett',
    host: 'Krista Tippett',
    description: 'Long conversations about meaning, faith, science and how to live, with an archive that goes back twenty years. New episodes now arrive in seasons.',
    category: 'Deep Conversation',
    character: 'xenia',
    episodeLength: '50–90 min',
    frequency: 'In seasons',
    whyListen: 'Tippett lets a guest finish a thought, then asks the question that takes it further. Listen to how she listens.',
    bestEpisode: 'Brené Brown: The Courage to Be Vulnerable',
    level: 'All levels',
    free: true,
  },
  {
    title: 'The Art of Charm',
    host: 'AJ Harbinger & Johnny Dzubak',
    description: 'Social skills, body language and conversation, from the two founders of a coaching company. Hour-long interviews alternate with short briefings.',
    category: 'Social Skills & Presence',
    character: 'theron',
    episodeLength: '10–15 min or about an hour',
    frequency: 'Twice a week',
    whyListen: 'Practical advice on how you come across in a conversation and under pressure.',
    bestEpisode: 'The Secrets of Body Language, with Joe Navarro (January 2025)',
    level: 'Beginner to Intermediate',
    free: true,
  },
];

const tedTalks = [
  {
    title: 'The Power of Vulnerability',
    speaker: 'Brené Brown',
    duration: '20 min, TEDxHouston 2010',
    character: 'philemon',
    description: 'Brown on what her interviews about connection taught her, and on the breakdown (her word) that the findings brought on. Watch how she uses humour and honesty together.',
    whyWatch: 'She tells a room of strangers that her own research sent her to a therapist, and the room laughs with her. That is what speaking honestly looks like.',
    url: 'https://www.ted.com/talks/brene_brown_the_power_of_vulnerability',
  },
  {
    title: 'Your Body Language May Shape Who You Are',
    speaker: 'Amy Cuddy',
    duration: '21 min, TEDGlobal 2012',
    character: 'theron',
    description: 'Cuddy\'s talk on "power posing". Watch it with the later evidence in mind: an upright, open posture makes people report feeling more confident; the famous hormone claims did not hold up. TED\'s own page now carries a note that the findings are disputed.',
    whyWatch: 'Never mind the hormones. Watch how she stands, and how she tells her own story near the end. That part needs no laboratory.',
    url: 'https://www.ted.com/talks/amy_cuddy_your_body_language_may_shape_who_you_are',
  },
  {
    title: 'How to Speak So That People Want to Listen',
    speaker: 'Julian Treasure',
    duration: '10 min, TEDGlobal 2013',
    character: 'theron',
    description: 'Treasure names seven habits that make people stop listening and four qualities that make them start, then opens the voice\'s toolbox: register, timbre, pace, pitch, volume and silence.',
    whyWatch: 'The most practical talk on this list. It ends with six warm-up exercises you can do in a corridor before you speak.',
    url: 'https://www.ted.com/talks/julian_treasure_how_to_speak_so_that_people_want_to_listen',
  },
  {
    title: 'The Power of Introverts',
    speaker: 'Susan Cain',
    duration: '19 min, TED2012',
    character: 'philemon',
    description: 'Cain argues that introverts bring talents the world should encourage, and that the best talker in a room does not always have the best ideas. Notice how she delivers it: quietly and without hurry.',
    whyWatch: 'She speaks softly and the hall listens anyway. That is the whole lesson.',
    url: 'https://www.ted.com/talks/susan_cain_the_power_of_introverts',
  },
  {
    title: 'The Secret Structure of Great Talks',
    speaker: 'Nancy Duarte',
    duration: '18 min, TEDxEast 2011',
    character: 'theron',
    description: 'Duarte lays out a structure that many great talks share, working through Martin Luther King\'s "I have a dream" speech and Steve Jobs\'s iPhone launch.',
    whyWatch: 'Structure is the cart that inspiration arrives on. Duarte shows you how to build the cart.',
    url: 'https://www.ted.com/talks/nancy_duarte_the_secret_structure_of_great_talks',
  },
  {
    title: 'How Great Leaders Inspire Action',
    speaker: 'Simon Sinek',
    duration: '18 min, TEDxPuget Sound 2009',
    character: 'xenia',
    description: 'Sinek\'s "golden circle", his model for why some leaders and organisations inspire and others do not. His line: people don\'t buy what you do; they buy why you do it.',
    whyWatch: 'Before you plan what to say, ask why you are saying it. The talk is one long argument for asking that first.',
    url: 'https://www.ted.com/talks/simon_sinek_how_great_leaders_inspire_action',
  },
  {
    title: 'The Danger of a Single Story',
    speaker: 'Chimamanda Ngozi Adichie',
    duration: '19 min, TEDGlobal 2009',
    character: 'xenia',
    description: 'Adichie warns that hearing only one story about a person or a country leads to misunderstanding. The trouble with a stereotype, she says, is that it is incomplete.',
    whyWatch: 'She builds the whole argument out of things that happened to her, one small story after another. Watch how little else she needs.',
    url: 'https://www.ted.com/talks/chimamanda_ngozi_adichie_the_danger_of_a_single_story',
  },
  {
    title: 'Before I Die I Want To…',
    speaker: 'Candy Chang',
    duration: '6 min, TEDGlobal 2012',
    character: 'philemon',
    description: 'After losing someone she loved, Chang turned the wall of an abandoned house in New Orleans into a chalkboard that asked her neighbours to finish one sentence. A short talk about grief and what a neighbourhood wrote back.',
    whyWatch: 'You do not need twenty minutes. She takes six, and nothing is missing.',
    url: 'https://www.ted.com/talks/candy_chang_before_i_die_i_want_to',
  },
  {
    title: '10 Ways to Have a Better Conversation',
    speaker: 'Celeste Headlee',
    duration: '11 min, TEDxCreativeCoast 2015',
    character: 'xenia',
    description: 'Ten rules for a better conversation from a long-time radio host. The first is not to multitask. The one she calls most important is to listen.',
    whyWatch: 'Headlee interviews people for a living, and her rules are plain enough to try at dinner tonight.',
    url: 'https://www.ted.com/talks/celeste_headlee_10_ways_to_have_a_better_conversation',
  },
];

const onlineResources = [
  {
    name: 'Toastmasters International',
    url: 'toastmasters.org',
    description: 'A worldwide network of clubs where members give prepared speeches, answer impromptu questions and give each other feedback. You can visit a club near you, or one that meets online, as a guest before you join.',
    category: 'Speaking Practice',
    cost: 'Free to visit, dues to join',
    credibility: 'Toastmasters International, first club formed in 1924',
    bestFeature: 'Table Topics, the part of a meeting where members answer a question off the cuff',
  },
  {
    name: 'TED playlist: The art of meaningful conversation',
    url: 'ted.com/playlists/211/the_art_of_conversation',
    description: 'A TED playlist of talks on speaking, listening and conversation, among them Julian Treasure, Celeste Headlee and Brené Brown.',
    category: 'Video Learning',
    cost: 'Free',
    credibility: 'TED',
    bestFeature: 'Short talks, from four minutes to about twenty',
  },
  {
    name: 'Program on Negotiation at Harvard Law School',
    url: 'pon.harvard.edu',
    description: 'Articles, free reports and videos on negotiation and resolving disputes, from a programme that brings together people from Harvard, MIT and Tufts.',
    category: 'Academic Resources',
    cost: 'Free articles, paid courses',
    credibility: 'Harvard Law School, founded 1983',
    bestFeature: 'The blog, which is free to read',
  },
  {
    name: 'Center for Nonviolent Communication',
    url: 'cnvc.org',
    description: 'The organisation Marshall Rosenberg founded in 1984. It offers free learning resources, workshops, longer trainings and a directory of certified trainers in Nonviolent Communication (NVC).',
    category: 'Empathy Training',
    cost: 'Free resources, paid trainings',
    credibility: 'Founded by Marshall Rosenberg',
    bestFeature: 'A map for finding a certified trainer near you',
  },
  {
    name: 'The School of Life',
    url: 'theschooloflife.com',
    description: 'Films, articles and books on self-understanding, calm and relationships, drawing on psychology, philosophy and culture.',
    category: 'Emotional Intelligence',
    cost: 'Free films, paid books',
    credibility: 'Started by the writer Alain de Botton',
    bestFeature: 'The short films on its YouTube channel',
  },
];

const courses = [
  {
    title: 'Crucial Conversations for Mastering Dialogue',
    provider: 'Crucial Learning (formerly VitalSmarts)',
    format: 'In person, live online or on demand',
    duration: '6–8 hours on demand',
    cost: 'Paid',
    description: 'The course built on the book. It teaches skills for noticing when a conversation matters, understanding what is at risk and keeping the dialogue open.',
    certification: false,
    level: 'All levels',
    outcomes: ['Spot the conversations that are keeping you stuck', 'Say a hard thing honestly and respectfully', 'Rebuild safety when the other person gets defensive'],
  },
  {
    title: 'Dynamic Public Speaking Specialization',
    provider: 'University of Washington, on Coursera',
    format: 'Online self-paced',
    duration: '4 courses, about 60 hours',
    cost: 'Subscription (free trial)',
    description: 'Four courses taught by Dr Matt McGarrity: an introduction, then speaking to inform, to persuade and to inspire. He says it is meant for nervous beginners, and it is built around practice speeches.',
    certification: true,
    level: 'Beginner',
    outcomes: ['Prepare speeches that inform, persuade and inspire', 'Develop your delivery: voice, movement and gesture', 'Grow in confidence through repeated practice'],
  },
  {
    title: 'The Science of Well-Being',
    provider: 'Yale University, on Coursera',
    format: 'Online self-paced',
    duration: '6 modules, about 10 hours',
    cost: 'Paid (free trial for some)',
    description: 'Not a speaking course. Laurie Santos sets out common mistaken beliefs about happiness and the research on what helps, with challenges that build better habits. We include it for the days when the nerves are about more than the speech.',
    certification: true,
    level: 'All levels',
    outcomes: ['See where common beliefs about happiness go wrong', 'Try the habits the course sets as challenges', 'Build one of them into your week'],
  },
];

const communities = [
  {
    name: 'Toastmasters International',
    type: 'Speaking clubs',
    description: 'Clubs that meet to practise. Members give prepared speeches, take a turn at impromptu questions, fill meeting roles and get feedback from a fellow member, working through a learning programme called Pathways.',
    locations: 'More than 13,800 clubs in nearly 150 countries',
    frequency: 'Set by each club',
    cost: 'Free as a guest, dues to join',
    benefit: 'A supportive room and feedback every time you speak',
  },
  {
    name: 'Authentic Relating Groups',
    type: 'Connection practice',
    description: 'Groups and courses that practise honest, attentive conversation through structured exercises. Some are run by training bodies and many are local and independent, so they vary a good deal. Treat them as practice, and look elsewhere for therapy.',
    locations: 'In many cities, and online',
    frequency: 'Varies by group',
    cost: 'Varies by group',
    benefit: 'Practice at saying what is true for you and at listening closely',
  },
  {
    name: 'The Moth StorySLAM',
    type: 'Storytelling',
    description: 'Open-mic nights where you put your name in the hat and, if it is drawn, tell a true story of no more than five minutes on the night\'s theme. Ten stories are told and scored by judges from the audience.',
    locations: 'Many US cities, plus London, Melbourne, Toronto, Johannesburg and Nairobi',
    frequency: 'See the events calendar',
    cost: 'Ticketed',
    benefit: 'Telling your own story to a live audience',
  },
  {
    name: 'Conversation Cafés',
    type: 'Community gatherings',
    description: 'A simple format for a hosted conversation, started by three friends in Seattle in 2001. A small group agrees to a few ground rules, such as listening to and respecting all points of view, and talks about something that matters. The hosting manual is free online.',
    locations: 'Wherever someone hosts one',
    frequency: 'Set by the host',
    cost: 'Free',
    benefit: 'Practise talking with strangers about things that matter',
  },
  {
    name: 'Your own practice circle',
    type: 'Peer practice groups',
    description: 'This one is our own suggestion, and no organisation runs it. Ask a few people who also want to speak better to meet regularly, take turns giving a short talk, and tell each other what worked.',
    locations: 'Self-organised, anywhere',
    frequency: 'Whatever the group agrees',
    cost: 'Free',
    benefit: 'Feedback from people who have heard you speak before',
  },
];

const digitalTools = [
  {
    name: 'Speeko',
    type: 'AI Speaking Coach',
    description: 'A speaking coach app that gives feedback on your pace, tone and filler words, with lessons and exercises. Like having a patient practice partner.',
    platforms: ['iOS', 'Android'],
    cost: 'Free to try, then subscription',
    useCase: 'Practise a speech and get feedback on your delivery',
    limitations: 'It measures the mechanics of delivery and nothing else',
  },
  {
    name: 'Orai',
    type: 'Speech Practice',
    description: 'Record yourself speaking and get automated feedback on filler words, pace, clarity and energy. It keeps your past sessions so you can see the trend.',
    platforms: ['iOS', 'Android'],
    cost: 'Free download, subscription inside',
    useCase: 'Daily speaking practice with measurable progress',
    limitations: 'The feedback comes from software, so treat the scores as a guide',
  },
  {
    name: 'Headspace',
    type: 'Mindfulness & Anxiety',
    description: 'Guided meditation, breathing exercises and courses on stress and anxiety. Nothing in it is about speaking. We list it for the nerves.',
    platforms: ['iOS', 'Android', 'Web'],
    cost: 'Subscription',
    useCase: 'A short breathing or meditation session before you speak',
    limitations: 'Not about speaking',
  },
  {
    name: 'Insight Timer',
    type: 'Meditation & Breathing',
    description: 'A meditation app with thousands of free guided meditations, including a topic on anxiety and stress.',
    platforms: ['iOS', 'Android'],
    cost: 'Free (premium available)',
    useCase: 'A short guided meditation before you speak',
    limitations: 'The library is large, so it helps to know what you are looking for',
  },
];

// Crisis support by country: the fleet's one table (src/data/crisisSupport.js).
// Nothing is typed here.
const crisisSupport = CRISIS_SUPPORT;

/* ─────────────────────────────────────────────
   FILTER HELPER
   ───────────────────────────────────────────── */

const filterBySearch = (items, term) => {
  if (!term) return items;
  const lower = term.toLowerCase();
  return items.filter((item) =>
    JSON.stringify(item).toLowerCase().includes(lower)
  );
};

/* ─────────────────────────────────────────────
   SECTIONS
   ───────────────────────────────────────────── */

const charCardStyles = (character) => {
  switch (character) {
    case 'philemon': return 'bg-philemon-bg/30 border-l-4 border-l-courtyard-olive';
    case 'xenia': return 'bg-xenia-bg/30 border-l-4 border-l-courtyard-aegean';
    case 'theron': return 'bg-theron-bg/30 border-l-4 border-l-courtyard-terracotta';
    default: return '';
  }
};

const charBadgeStyles = (character) => {
  switch (character) {
    case 'philemon': return 'bg-courtyard-olive/10 text-courtyard-olive-dark';
    case 'xenia': return 'bg-courtyard-aegean/10 text-courtyard-aegean-dark';
    case 'theron': return 'bg-courtyard-terracotta/10 text-courtyard-clay';
    default: return 'bg-courtyard-terracotta/10 text-courtyard-clay';
  }
};

const BookCard = ({ book, savedResources, onSave }) => (
  <Card className={`p-5 h-full hover:shadow-courtyard-md hover:-translate-y-0.5 transition-all ${charCardStyles(book.character)}`}>
    <div className="flex justify-between items-start mb-3">
      <div className="flex-1 min-w-0">
        <h4 className="font-display font-bold text-courtyard-stone leading-tight">{book.title}</h4>
        <p className="text-sm text-courtyard-stone/80 mt-0.5">by {book.author} ({book.year})</p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className={`text-xs px-2 py-0.5 rounded ${charBadgeStyles(book.character)}`}>{book.category}</span>
          <span className="text-xs text-courtyard-stone/80">{book.difficulty}</span>
        </div>
      </div>
    </div>

    <p className="text-sm text-courtyard-stone/80 mb-3 leading-relaxed">{book.description}</p>

    <div className="space-y-2">
      <div className="p-2.5 bg-courtyard-olive/5 rounded-lg border border-courtyard-olive/15">
        <p className="text-xs text-courtyard-olive-dark font-medium mb-0.5">Why Read This:</p>
        <p className="text-xs text-courtyard-stone/80">{book.whyRead}</p>
      </div>
      <div className="p-2.5 bg-courtyard-gold/5 rounded-lg border border-courtyard-gold/15">
        <p className="text-xs text-courtyard-gold-ink font-medium mb-0.5">Key Takeaway:</p>
        <p className="text-xs text-courtyard-stone/80 italic">{book.keyTakeaway}</p>
      </div>
    </div>

    <div className="flex items-center justify-between mt-3">
      <div className="flex items-center gap-1.5">
        <CharacterAvatar characterId={book.character} size="xs" />
        <span className="text-xs text-courtyard-stone/80 capitalize">{book.character}</span>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onSave(book.title)}
          disabled={savedResources.includes(book.title)}
          className="text-xs text-courtyard-stone/80 hover:text-courtyard-clay flex items-center gap-1"
        >
          <Bookmark className="w-3 h-3" aria-hidden="true" />
          {savedResources.includes(book.title) ? 'Saved' : 'Save'}
        </button>
        {book.url && (
          <a href={book.url} target="_blank" rel="noopener noreferrer" className="text-xs text-courtyard-stone/80 hover:text-courtyard-clay flex items-center gap-1">
            <ExternalLink className="w-3 h-3" aria-hidden="true" />
            Find this book
          </a>
        )}
      </div>
    </div>
  </Card>
);

const BookSection = ({ searchTerm, savedResources, onSave }) => {
  const sections = [
    { key: 'essential', label: 'Essential Reading', icon: Star, items: books.essential, colour: 'text-courtyard-gold-ink', bg: 'bg-courtyard-gold/10', border: 'border-courtyard-gold/20' },
    { key: 'fearAndConfidence', label: 'Fear & Confidence', icon: Brain, items: books.fearAndConfidence, colour: 'text-courtyard-olive-dark', bg: 'bg-philemon-bg/40', border: 'border-courtyard-olive/20' },
    { key: 'craftAndStructure', label: 'Craft & Structure', icon: BookOpen, items: books.craftAndStructure, colour: 'text-courtyard-clay', bg: 'bg-theron-bg/40', border: 'border-courtyard-terracotta/20' },
    { key: 'connectionAndListening', label: 'Connection & Listening', icon: Users, items: books.connectionAndListening, colour: 'text-courtyard-aegean-dark', bg: 'bg-xenia-bg/40', border: 'border-courtyard-aegean/20' },
  ];

  return (
    <div className="space-y-8">
      {sections.map((section) => {
        const filtered = filterBySearch(section.items, searchTerm);
        if (filtered.length === 0) return null;
        const Icon = section.icon;
        return (
          <div key={section.key}>
            <div className={`rounded-xl px-4 py-3 mb-4 ${section.bg} border ${section.border}`}>
              <h3 className="font-display text-lg font-bold text-courtyard-stone flex items-center gap-2">
                <Icon className={`w-5 h-5 ${section.colour}`} aria-hidden="true" />
                {section.label}
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filtered.map((book, i) => (
                <BookCard key={i} book={book} savedResources={savedResources} onSave={onSave} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PodcastSection = ({ searchTerm }) => (
  <div className="space-y-6">
    <div className="p-4 bg-theron-bg/30 rounded-xl border border-courtyard-terracotta/20 text-sm text-courtyard-stone/80">
      <Headphones className="w-4 h-4 inline mr-2 text-courtyard-clay" aria-hidden="true" />
      <strong>Pro tip:</strong> Listen for more than the content. Notice how these hosts work: the questions they ask, where they pause, how their tone shifts.
    </div>
    <div className="grid gap-4">
      {filterBySearch(podcasts, searchTerm).map((podcast, i) => (
        <Card key={i} className={`p-5 hover:-translate-y-0.5 transition-all ${charCardStyles(podcast.character)}`}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-display font-bold text-courtyard-stone flex items-center gap-2">
                <Mic className={`w-4 h-4 ${podcast.character === 'philemon' ? 'text-courtyard-olive-dark' : podcast.character === 'xenia' ? 'text-courtyard-aegean-dark' : 'text-courtyard-clay'}`} aria-hidden="true" />
                {podcast.title}
              </h4>
              <p className="text-xs text-courtyard-stone/80">Hosted by {podcast.host}</p>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-courtyard-stone/80">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" aria-hidden="true" />{podcast.episodeLength}</span>
                <span>{podcast.frequency}</span>
                {podcast.free && <span className="text-courtyard-olive-dark">Free</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CharacterAvatar characterId={podcast.character} size="xs" />
              <span className={`text-xs px-2 py-0.5 rounded ${charBadgeStyles(podcast.character)}`}>{podcast.level}</span>
            </div>
          </div>
          <p className="text-sm text-courtyard-stone/80 mb-3">{podcast.description}</p>
          <div className="space-y-2">
            <div className="p-2.5 bg-courtyard-olive/5 rounded-lg border border-courtyard-olive/15">
              <p className="text-xs text-courtyard-olive-dark font-medium mb-0.5">Why Listen:</p>
              <p className="text-xs text-courtyard-stone/80">{podcast.whyListen}</p>
            </div>
            <div className="p-2.5 bg-courtyard-gold/5 rounded-lg border border-courtyard-gold/15">
              <p className="text-xs text-courtyard-gold-ink font-medium mb-0.5">Start With:</p>
              <p className="text-xs text-courtyard-stone/80">{podcast.bestEpisode}</p>
            </div>
          </div>
        </Card>
      ))}
    </div>
  </div>
);

const TalksSection = ({ searchTerm }) => (
  <div className="space-y-6">
    <div className="p-4 bg-xenia-bg/30 rounded-xl border border-courtyard-aegean/20 text-sm text-courtyard-stone/80">
      <Video className="w-4 h-4 inline mr-2 text-courtyard-aegean-dark" aria-hidden="true" />
      <strong>Study tip:</strong> Watch each talk twice: once for what is said, and once for how. Notice the pauses, the pace and what the speaker does with their body.
    </div>
    <div className="grid gap-4">
      {filterBySearch(tedTalks, searchTerm).map((talk, i) => (
        <a key={i} href={talk.url} target="_blank" rel="noopener noreferrer" className="block">
          <Card className={`p-5 hover:shadow-courtyard-md hover:-translate-y-0.5 transition-all ${charCardStyles(talk.character)}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h4 className="font-display font-bold text-courtyard-stone leading-tight">{talk.title}</h4>
                <p className="text-sm text-courtyard-stone/80 mt-0.5">{talk.speaker}</p>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-courtyard-stone/80">
                  <span>{talk.duration}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <CharacterAvatar characterId={talk.character} size="xs" />
                <ExternalLink className="w-4 h-4 text-courtyard-stone/80" aria-hidden="true" />
              </div>
            </div>
            <p className="text-sm text-courtyard-stone/80 mb-2 leading-relaxed">{talk.description}</p>
            <div className="p-2.5 bg-courtyard-olive/5 rounded-lg border border-courtyard-olive/15">
              <p className="text-xs text-courtyard-olive-dark font-medium mb-0.5">Why Watch:</p>
              <p className="text-xs text-courtyard-stone/80">{talk.whyWatch}</p>
            </div>
          </Card>
        </a>
      ))}
    </div>
  </div>
);

const onlineCardColours = ['bg-philemon-bg/30 border-l-4 border-l-courtyard-olive', 'bg-xenia-bg/30 border-l-4 border-l-courtyard-aegean', 'bg-theron-bg/30 border-l-4 border-l-courtyard-terracotta'];

const OnlineSection = ({ searchTerm }) => (
  <div className="grid gap-4">
    {filterBySearch(onlineResources, searchTerm).map((resource, i) => (
      <Card key={i} className={`p-5 hover:-translate-y-0.5 transition-all ${onlineCardColours[i % 3]}`}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="font-display font-bold text-courtyard-stone">{resource.name}</h4>
            <p className="text-xs text-courtyard-clay">{resource.category}</p>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded ${resource.cost === 'Free' ? 'bg-courtyard-olive/10 text-courtyard-olive-dark' : 'bg-courtyard-gold/10 text-courtyard-gold-ink'}`}>
            {resource.cost}
          </span>
        </div>
        <p className="text-sm text-courtyard-stone/80 mb-3">{resource.description}</p>
        <div className="flex items-center gap-4 text-xs text-courtyard-stone/80 mb-3">
          <span>Source: {resource.credibility}</span>
        </div>
        <div className="p-2.5 bg-courtyard-gold/5 rounded-lg border border-courtyard-gold/15 mb-3">
          <p className="text-xs text-courtyard-gold-ink"><strong>Best Feature:</strong> {resource.bestFeature}</p>
        </div>
        <a href={`https://${resource.url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-courtyard-clay hover:underline flex items-center gap-1">
          <ExternalLink className="w-3 h-3" aria-hidden="true" /> Visit Resource
        </a>
      </Card>
    ))}
  </div>
);

const CoursesSection = ({ searchTerm }) => (
  <div className="grid gap-4">
    {filterBySearch(courses, searchTerm).map((course, i) => (
      <Card key={i} className={`p-5 hover:-translate-y-0.5 transition-all ${onlineCardColours[i % 3]}`}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="font-display font-bold text-courtyard-stone">{course.title}</h4>
            <p className="text-xs text-courtyard-stone/80">{course.provider}</p>
            <div className="flex items-center gap-3 mt-1.5 text-xs text-courtyard-stone/80">
              <span>{course.format}</span>
              <span>{course.duration}</span>
              {course.certification && (
                <span className="flex items-center gap-1 text-courtyard-olive-dark"><Award className="w-3 h-3" aria-hidden="true" />Certificate</span>
              )}
            </div>
          </div>
          <span className="text-sm font-bold text-courtyard-gold-ink">
            {course.cost === 'Free' ? 'Free' : course.cost}
          </span>
        </div>
        <p className="text-sm text-courtyard-stone/80 mb-3">{course.description}</p>
        <div className="p-2.5 bg-courtyard-olive/5 rounded-lg border border-courtyard-olive/15">
          <p className="text-xs text-courtyard-olive-dark font-medium mb-1.5">Learning Outcomes:</p>
          <ul className="space-y-1">
            {course.outcomes.map((outcome, j) => (
              <li key={j} className="text-xs text-courtyard-stone/80 flex items-start gap-1.5">
                <ChevronRight className="w-3 h-3 text-courtyard-olive-dark mt-0.5 flex-shrink-0" aria-hidden="true" />
                {outcome}
              </li>
            ))}
          </ul>
        </div>
      </Card>
    ))}
  </div>
);

const CommunitiesSection = ({ searchTerm }) => (
  <div className="space-y-6">
    <div className="p-4 bg-philemon-bg/30 rounded-xl border border-courtyard-olive/20 text-sm text-courtyard-stone/80">
      <Users className="w-4 h-4 inline mr-2 text-courtyard-olive-dark" aria-hidden="true" />
      <strong>Remember:</strong> Speaking improves with speaking. These are places to practise among people who are there for the same reason.
    </div>
    <div className="grid gap-4">
      {filterBySearch(communities, searchTerm).map((community, i) => (
        <Card key={i} className={`p-5 hover:-translate-y-0.5 transition-all ${onlineCardColours[i % 3]}`}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-display font-bold text-courtyard-stone">{community.name}</h4>
              <p className="text-xs text-courtyard-clay">{community.type}</p>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-courtyard-stone/80">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" aria-hidden="true" />{community.locations}</span>
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" aria-hidden="true" />{community.frequency}</span>
              </div>
            </div>
            <span className="text-xs text-courtyard-olive-dark">{community.cost}</span>
          </div>
          <p className="text-sm text-courtyard-stone/80 mb-3">{community.description}</p>
          <div className="p-2.5 bg-courtyard-gold/5 rounded-lg border border-courtyard-gold/15">
            <p className="text-xs text-courtyard-gold-ink"><strong>Key Benefit:</strong> {community.benefit}</p>
          </div>
        </Card>
      ))}
    </div>
  </div>
);

const ToolsSection = ({ searchTerm }) => (
  <div className="grid gap-4">
    {filterBySearch(digitalTools, searchTerm).map((tool, i) => (
      <Card key={i} className={`p-5 hover:-translate-y-0.5 transition-all ${onlineCardColours[i % 3]}`}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="font-display font-bold text-courtyard-stone flex items-center gap-2">
              <Smartphone className="w-4 h-4 text-courtyard-clay" aria-hidden="true" />
              {tool.name}
            </h4>
            <p className="text-xs text-courtyard-stone/80">{tool.type}</p>
            <div className="flex items-center gap-1.5 mt-1.5">
              {tool.platforms.map((p) => (
                <span key={p} className="text-xs px-1.5 py-0.5 bg-courtyard-stone/5 text-courtyard-stone/80 rounded">{p}</span>
              ))}
            </div>
          </div>
          <div className="text-right">
            <span className={`text-xs ${tool.cost === 'Free' ? 'text-courtyard-olive-dark' : 'text-courtyard-gold-ink'}`}>{tool.cost}</span>
          </div>
        </div>
        <p className="text-sm text-courtyard-stone/80 mb-3">{tool.description}</p>
        <div className="space-y-2">
          <div className="p-2 bg-courtyard-olive/5 rounded-lg border border-courtyard-olive/15">
            <p className="text-xs text-courtyard-olive-dark"><strong>Use For:</strong> {tool.useCase}</p>
          </div>
          <div className="p-2 bg-courtyard-gold/5 rounded-lg border border-courtyard-gold/15">
            <p className="text-xs text-courtyard-gold-ink"><strong>Limitations:</strong> {tool.limitations}</p>
          </div>
        </div>
      </Card>
    ))}
  </div>
);

const CrisisSection = () => {
  const [activeCountry, setActiveCountry] = useState(DEFAULT_COUNTRY);

  return (
    <div className="space-y-6">
      <Card className="p-5 bg-red-50/50 border-red-200/30">
        <h3 className="font-display text-lg font-bold text-courtyard-stone mb-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-700" aria-hidden="true" />
          Crisis Support Resources
        </h3>
        <p className="text-sm text-courtyard-stone/80 mb-4">
          If exploring your voice and emotions brings up difficult feelings, or if someone you speak with is in crisis,
          the services below are there to help.
        </p>

        <div className="flex flex-wrap gap-2 mb-5">
          {Object.entries(crisisSupport).map(([key, country]) => (
            <button
              key={key}
              onClick={() => setActiveCountry(key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeCountry === key
                  ? 'bg-courtyard-terracotta text-white'
                  : 'bg-courtyard-stone/5 text-courtyard-stone/80 hover:bg-courtyard-stone/10'
              }`}
            >
              {country.flag} {country.name}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {crisisSupport[activeCountry].services.map((service, i) => (
            <div key={i} className="p-3 bg-white/60 rounded-lg border border-courtyard-gold/15">
              <div className="flex items-start justify-between mb-1">
                <div>
                  <h4 className="font-medium text-courtyard-stone text-sm">{service.name}</h4>
                  <p className="text-xs text-courtyard-stone/80">{service.description}</p>
                </div>
                <span className={`text-xs px-1.5 py-0.5 rounded ${service.type === 'call' ? 'bg-courtyard-olive/10 text-courtyard-olive-dark' : 'bg-courtyard-terracotta/10 text-courtyard-clay'}`}>
                  {service.type}
                </span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="flex items-center gap-1.5 text-sm font-bold text-courtyard-clay">
                  <Phone className="w-3.5 h-3.5" aria-hidden="true" />
                  {service.number}
                </span>
                {service.website && (
                  <a href={`https://${service.website}`} target="_blank" rel="noopener noreferrer" className="text-xs text-courtyard-stone/80 hover:text-courtyard-clay flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" aria-hidden="true" /> Website
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200/30">
          <p className="text-xs text-red-700 font-medium mb-1"><AlertCircle className="w-3.5 h-3.5 inline mr-1" aria-hidden="true" />Emergency Services</p>
          <p className="text-xs text-courtyard-stone/80">In a life-threatening emergency, call {crisisSupport[activeCountry].emergency.number} ({crisisSupport[activeCountry].name}). In another country, call your local emergency number.</p>
        </div>
      </Card>

      <Card className="p-5 bg-courtyard-gold/5 border-courtyard-gold/15">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-courtyard-clay mt-0.5" aria-hidden="true" />
          <div>
            <h4 className="font-display font-bold text-courtyard-stone text-sm mb-1.5">Your Role</h4>
            <p className="text-xs text-courtyard-stone/80 mb-2">If a conversation reveals someone is struggling:</p>
            <ul className="space-y-1.5">
              {[
                'Listen without judgement and take them seriously',
                'You don\'t need to be a therapist or fix anything',
                'Being a compassionate bridge to professional help is enough',
                'Offer to help them contact one of the services above',
              ].map((item, i) => (
                <li key={i} className="text-xs text-courtyard-stone/80 flex items-start gap-1.5">
                  <ChevronRight className="w-3 h-3 text-courtyard-clay mt-0.5 flex-shrink-0" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

/* ─────────────────────────────────────────────
   CATEGORIES
   ───────────────────────────────────────────── */

const CATEGORIES = [
  { id: 'books', label: 'Books', icon: BookOpen, colour: 'text-courtyard-olive-dark', activeBg: 'bg-courtyard-olive/15' },
  { id: 'podcasts', label: 'Podcasts', icon: Headphones, colour: 'text-courtyard-clay', activeBg: 'bg-courtyard-terracotta/15' },
  { id: 'talks', label: 'TED Talks', icon: Video, colour: 'text-courtyard-aegean-dark', activeBg: 'bg-courtyard-aegean/15' },
  { id: 'online', label: 'Online', icon: Globe, colour: 'text-courtyard-gold-ink', activeBg: 'bg-courtyard-gold/15' },
  { id: 'courses', label: 'Courses', icon: Award, colour: 'text-courtyard-olive-dark', activeBg: 'bg-courtyard-olive/15' },
  { id: 'communities', label: 'Communities', icon: Users, colour: 'text-courtyard-aegean-dark', activeBg: 'bg-courtyard-aegean/15' },
  { id: 'apps', label: 'Apps', icon: Smartphone, colour: 'text-courtyard-clay', activeBg: 'bg-courtyard-terracotta/15' },
  { id: 'crisis', label: 'Crisis Support', icon: AlertCircle, colour: 'text-red-700', activeBg: 'bg-red-50' },
];

/* ─────────────────────────────────────────────
   PAGE COMPONENT
   ───────────────────────────────────────────── */

const ResourcesPage = () => {
  const [activeCategory, setActiveCategory] = useState('books');
  const [searchTerm, setSearchTerm] = useState('');
  const [savedResources, setSavedResources] = useState([]);
  const { markResourcesVisited, getAchievementContext } = useProgress();
  const { checkAchievements } = useGamification();

  useEffect(() => {
    markResourcesVisited();
    checkAchievements(getAchievementContext({ resourcesVisited: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = (title) => {
    if (!savedResources.includes(title)) {
      setSavedResources([...savedResources, title]);
    }
  };

  const renderSection = () => {
    switch (activeCategory) {
      case 'books':
        return <BookSection searchTerm={searchTerm} savedResources={savedResources} onSave={handleSave} />;
      case 'podcasts':
        return <PodcastSection searchTerm={searchTerm} />;
      case 'talks':
        return <TalksSection searchTerm={searchTerm} />;
      case 'online':
        return <OnlineSection searchTerm={searchTerm} />;
      case 'courses':
        return <CoursesSection searchTerm={searchTerm} />;
      case 'communities':
        return <CommunitiesSection searchTerm={searchTerm} />;
      case 'apps':
        return <ToolsSection searchTerm={searchTerm} />;
      case 'crisis':
        return <CrisisSection />;
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Resources | Finding Your Voice</title>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-10">
        {/* Header */}
        <motion.div
          className="text-center mb-8 rounded-2xl bg-gradient-to-br from-xenia-bg/40 via-courtyard-parchment to-philemon-bg/30 border border-courtyard-aegean/15 px-6 py-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-display text-3xl md:text-4xl font-bold text-courtyard-stone mb-3">
            Resources
          </h1>
          <p className="text-courtyard-stone/80 max-w-xl mx-auto leading-relaxed">
            Books, podcasts, talks, courses and communities to take your practice further.
            Finding your voice is slow work, and these are good company for it.
          </p>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <div className="relative max-w-sm mx-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-courtyard-stone/80" aria-hidden="true" />
            <input
              type="text"
              placeholder="Search resources…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-white/60 border border-courtyard-gold/20 rounded-lg text-sm text-courtyard-stone placeholder-courtyard-stone/30 focus:outline-none focus:ring-2 focus:ring-courtyard-terracotta/30"
            />
          </div>
        </motion.div>

        {/* Category Tabs */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="flex flex-wrap justify-center gap-2">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-3.5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                    activeCategory === cat.id
                      ? `${cat.activeBg} ${cat.colour} shadow-sm`
                      : 'bg-courtyard-stone/5 text-courtyard-stone/80 hover:bg-courtyard-stone/10'
                  }`}
                >
                  <Icon className="w-4 h-4" aria-hidden="true" />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Saved Count */}
        {savedResources.length > 0 && (
          <div className="text-center mb-4">
            <span className="text-xs text-courtyard-stone/80">
              <Bookmark className="w-3 h-3 inline mr-1" aria-hidden="true" />
              {savedResources.length} saved
            </span>
          </div>
        )}

        {/* Content */}
        <motion.div
          key={activeCategory}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {renderSection()}
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-12"
        >
          <Card className="p-6 bg-courtyard-gold/5 border-courtyard-gold/15 text-center">
            <Coffee className="w-6 h-6 text-courtyard-clay mx-auto mb-2" aria-hidden="true" />
            <h3 className="font-display font-bold text-courtyard-stone mb-2">
              The Best Resource? Practice.
            </h3>
            <p className="text-sm text-courtyard-stone/80 max-w-lg mx-auto">
              All the books and podcasts in the world will do less for your speaking than one real,
              slightly scary, honest conversation today. Find someone. Say something true.
            </p>
          </Card>
        </motion.div>
      </div>
    </>
  );
};

export default ResourcesPage;
